// src/components/BookingConfirmationModal.jsx
// ═══════════════════════════════════════════════════════════════════════════════
// BOOKING CONFIRMATION MODAL
// Shows selected package with individual booking buttons + Keep Itinerary options
// ═══════════════════════════════════════════════════════════════════════════════

import React, { useState } from 'react';
import './BookingConfirmationModal.css';

// ═══════════════════════════════════════════════════════════════════════════════
// SAFE VALUE HELPERS
// ═══════════════════════════════════════════════════════════════════════════════
const safeString = (val, fallback = '') => {
  if (val === null || val === undefined) return fallback;
  if (typeof val === 'string') return val;
  if (typeof val === 'number') return String(val);
  if (typeof val === 'object') return fallback;
  return String(val) || fallback;
};

const safeNumber = (val, fallback = 0) => {
  if (val === null || val === undefined) return fallback;
  if (typeof val === 'number' && !isNaN(val)) return val;
  const parsed = parseFloat(val);
  return isNaN(parsed) ? fallback : parsed;
};

const BookingConfirmationModal = ({
  isOpen,
  onClose,
  packageData,
  searchParams = {},
  hotelParams = {}
}) => {
  const [emailSent, setEmailSent] = useState(false);
  const [email, setEmail] = useState('');
  const [linkCopied, setLinkCopied] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);

  if (!isOpen || !packageData) return null;

  // ═══════════════════════════════════════════════════════════════════════════════
  // EXTRACT PACKAGE DATA SAFELY
  // ═══════════════════════════════════════════════════════════════════════════════
  const {
    outboundFlight,
    returnFlight,
    hotels = [],
    flightTotal = 0,
    hotelTotal = 0,
    grandTotal = 0,
    passengers = 1
  } = packageData;

  // Flight data extraction
  const getFlightInfo = (flight) => {
    if (!flight) return null;
    
    const leg = flight.outbound || flight;
    const returnLeg = flight.return;
    
    // Airline
    let airlineName = 'Airline';
    let airlineCode = '';
    if (typeof flight.airline === 'object' && flight.airline !== null) {
      airlineName = safeString(flight.airline.name) || safeString(flight.airline.code) || 'Airline';
      airlineCode = safeString(flight.airline.code);
    } else {
      airlineName = safeString(flight.airline) || 'Airline';
    }

    // Format time from ISO
    const formatTime = (isoString) => {
      if (!isoString) return '--:--';
      try {
        const date = new Date(isoString);
        if (isNaN(date.getTime())) return '--:--';
        return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
      } catch {
        return '--:--';
      }
    };

    // Format date
    const formatDate = (isoString) => {
      if (!isoString) return '';
      try {
        const date = new Date(isoString);
        if (isNaN(date.getTime())) return '';
        return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
      } catch {
        return '';
      }
    };

    // Duration format
    const formatDuration = (dur) => {
      if (!dur) return '';
      if (typeof dur === 'string' && dur.startsWith('PT')) {
        const hours = dur.match(/(\d+)H/)?.[1] || '0';
        const mins = dur.match(/(\d+)M/)?.[1] || '0';
        return `${hours}h ${mins}m`;
      }
      return safeString(dur);
    };

    const outboundInfo = {
      origin: safeString(leg?.origin) || safeString(searchParams?.origin) || '---',
      destination: safeString(leg?.destination) || safeString(searchParams?.destination) || '---',
      departure: formatTime(leg?.departure),
      arrival: formatTime(leg?.arrival),
      date: formatDate(leg?.departure) || formatDate(searchParams?.departureDate),
      duration: formatDuration(leg?.duration),
      stops: leg?.stops || 0
    };

    const returnInfo = returnLeg ? {
      origin: safeString(returnLeg?.origin) || outboundInfo.destination,
      destination: safeString(returnLeg?.destination) || outboundInfo.origin,
      departure: formatTime(returnLeg?.departure),
      arrival: formatTime(returnLeg?.arrival),
      date: formatDate(returnLeg?.departure) || formatDate(searchParams?.returnDate),
      duration: formatDuration(returnLeg?.duration),
      stops: returnLeg?.stops || 0
    } : null;

    // Get booking links
    const bookingLinks = flight.bookingLinks || [];
    const deepLink = flight.deepLink || null;

    return {
      airlineName,
      airlineCode,
      outbound: outboundInfo,
      return: returnInfo,
      isRoundTrip: !!returnLeg,
      bookingLinks,
      deepLink
    };
  };

  // Hotel data extraction
  const getHotelInfo = (hotel) => {
    if (!hotel) return null;

    const name = safeString(hotel.name) || 'Hotel';
    const city = safeString(hotel.city) || '';
    const stars = Math.min(Math.max(parseInt(hotel.stars) || 4, 1), 5);
    const rating = safeNumber(hotel.rating) || safeNumber(hotel.guestRating);
    const pricePerNight = safeNumber(hotel.price) || safeNumber(hotel.pricePerNight);
    const nights = hotel.nights || 1;
    const totalPrice = pricePerNight * nights;

    // Location - handle object safely
    let locationText = city;
    if (typeof hotel.location === 'object' && hotel.location !== null) {
      const downtown = safeString(hotel.location.downtownName);
      if (downtown) locationText = `${downtown}, ${city}`;
    } else if (typeof hotel.location === 'string') {
      locationText = hotel.location;
    }

    // Dates
    const checkIn = hotel.dates?.start || hotelParams?.checkIn || searchParams?.departureDate || '';
    const checkOut = hotel.dates?.end || hotelParams?.checkOut || searchParams?.returnDate || '';

    // Booking links
    const bookingLinks = hotel.bookingLinks || [];

    return {
      name,
      city,
      locationText,
      stars,
      rating,
      pricePerNight,
      nights,
      totalPrice,
      checkIn,
      checkOut,
      bookingLinks
    };
  };

  const flightInfo = getFlightInfo(outboundFlight);
  const hotelList = hotels.map(h => getHotelInfo(h)).filter(Boolean);

  // ═══════════════════════════════════════════════════════════════════════════════
  // HANDLERS
  // ═══════════════════════════════════════════════════════════════════════════════
  
  const handleBookFlight = (link) => {
    const url = link?.url || flightInfo?.deepLink;
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleBookHotel = (hotel, link) => {
    const url = link?.url;
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleSendEmail = async () => {
    if (!email || !email.includes('@')) {
      alert('Please enter a valid email address');
      return;
    }

    setSendingEmail(true);
    
    try {
      // Build itinerary data
      const itineraryData = {
        email,
        tripSummary: {
          destination: flightInfo?.outbound?.destination || 'Trip',
          departureDate: searchParams?.departureDate,
          returnDate: searchParams?.returnDate,
          passengers
        },
        flight: flightInfo ? {
          airline: flightInfo.airlineName,
          route: `${flightInfo.outbound.origin} → ${flightInfo.outbound.destination}`,
          isRoundTrip: flightInfo.isRoundTrip,
          price: flightTotal
        } : null,
        hotels: hotelList.map(h => ({
          name: h.name,
          location: h.locationText,
          nights: h.nights,
          price: h.totalPrice
        })),
        totalPrice: grandTotal
      };

      // Call email API (you'll need to implement this endpoint)
      const response = await fetch('/api/send-itinerary-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(itineraryData)
      });

      if (response.ok) {
        setEmailSent(true);
        setTimeout(() => setEmailSent(false), 3000);
      } else {
        // Fallback: open mailto
        const subject = `Your Trip Itinerary - ${flightInfo?.outbound?.destination || 'Trip'}`;
        const body = generateEmailBody();
        window.open(`mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`);
        setEmailSent(true);
        setTimeout(() => setEmailSent(false), 3000);
      }
    } catch (error) {
      // Fallback to mailto
      const subject = `Your Trip Itinerary - ${flightInfo?.outbound?.destination || 'Trip'}`;
      const body = generateEmailBody();
      window.open(`mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`);
      setEmailSent(true);
      setTimeout(() => setEmailSent(false), 3000);
    }

    setSendingEmail(false);
  };

  const generateEmailBody = () => {
    let body = `YOUR TRIP ITINERARY\n`;
    body += `==================\n\n`;

    if (flightInfo) {
      body += `✈️ FLIGHT\n`;
      body += `${flightInfo.airlineName}\n`;
      body += `${flightInfo.outbound.origin} → ${flightInfo.outbound.destination}\n`;
      if (flightInfo.isRoundTrip) body += `Round Trip\n`;
      body += `Date: ${flightInfo.outbound.date}\n`;
      body += `Price: $${flightTotal.toLocaleString()}\n\n`;
    }

    hotelList.forEach((hotel, idx) => {
      body += `🏨 HOTEL${hotelList.length > 1 ? ` ${idx + 1}` : ''}\n`;
      body += `${hotel.name}\n`;
      body += `${hotel.locationText}\n`;
      body += `${hotel.nights} nights\n`;
      body += `Price: $${hotel.totalPrice.toLocaleString()}\n\n`;
    });

    body += `==================\n`;
    body += `TOTAL: $${grandTotal.toLocaleString()}\n\n`;
    body += `Powered by Airgati - airgati.com`;

    return body;
  };

  const handleCopyLink = async () => {
    try {
      // Generate a shareable link (you could encode the trip data in URL params or use a short link service)
      const tripData = {
        f: flightInfo?.outbound?.origin,
        t: flightInfo?.outbound?.destination,
        d: searchParams?.departureDate,
        r: searchParams?.returnDate,
        p: passengers
      };
      
      const params = new URLSearchParams();
      Object.entries(tripData).forEach(([k, v]) => {
        if (v) params.set(k, v);
      });

      const shareUrl = `${window.location.origin}?${params.toString()}`;
      
      await navigator.clipboard.writeText(shareUrl);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (error) {
      // Fallback
      alert('Link copied to clipboard!');
    }
  };

  const handleDownloadPDF = () => {
    // Generate PDF content using browser print
    const printContent = generatePrintableContent();
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  const generatePrintableContent = () => {
    const destination = flightInfo?.outbound?.destination || 'Trip';
    
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Trip Itinerary - ${destination}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 40px; color: #18181b; }
          .header { display: flex; justify-content: space-between; align-items: center; border-bottom: 3px solid #fbbf24; padding-bottom: 20px; margin-bottom: 30px; }
          .logo { font-size: 24px; font-weight: 700; }
          .ref { text-align: right; font-size: 12px; color: #6b7280; }
          .ref-code { font-size: 14px; font-weight: 700; font-family: monospace; }
          .trip-summary { background: #f8fafc; border-radius: 8px; padding: 20px; margin-bottom: 24px; }
          .trip-title { font-size: 20px; font-weight: 700; margin-bottom: 8px; }
          .trip-meta { display: flex; gap: 20px; font-size: 13px; color: #4b5563; }
          .section { margin-bottom: 24px; }
          .section-title { font-size: 14px; color: #6b7280; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 12px; border-bottom: 1px solid #e5e7eb; padding-bottom: 8px; }
          table { width: 100%; font-size: 13px; border-collapse: collapse; }
          td { padding: 8px 0; }
          td:first-child { color: #6b7280; width: 120px; }
          td:last-child { color: #18181b; font-weight: 500; }
          .cost-row { background: #f0fdf4; }
          .cost-row td { padding: 12px 8px; }
          .cost-row td:first-child { color: #065f46; font-weight: 600; }
          .cost-row td:last-child { color: #059669; font-weight: 700; font-size: 16px; }
          .total-section { background: linear-gradient(135deg, #ecfdf5, #d1fae5); border: 2px solid #10b981; border-radius: 8px; padding: 20px; margin-top: 20px; }
          .total-section table td { padding: 4px 0; color: #065f46; }
          .total-section .grand-total td { padding-top: 12px; border-top: 2px solid #10b981; }
          .total-section .grand-total td:first-child { font-weight: 700; font-size: 18px; }
          .total-section .grand-total td:last-child { font-weight: 700; font-size: 24px; color: #059669; }
          .footer { margin-top: 30px; padding-top: 20px; border-top: 1px solid #e5e7eb; text-align: center; color: #9ca3af; font-size: 11px; }
          @media print { body { padding: 20px; } }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="logo">✈️ Airgati</div>
          <div class="ref">
            <div>Booking Reference</div>
            <div class="ref-code">AG-${Date.now().toString(36).toUpperCase()}</div>
          </div>
        </div>

        <div class="trip-summary">
          <div class="trip-title">🗽 ${destination} Trip</div>
          <div class="trip-meta">
            <span>📅 ${searchParams?.departureDate || ''} - ${searchParams?.returnDate || ''}</span>
            <span>👥 ${passengers} Traveler${passengers > 1 ? 's' : ''}</span>
          </div>
        </div>

        ${flightInfo ? `
        <div class="section">
          <div class="section-title">✈️ Flight Details</div>
          <table>
            <tr><td>Airline:</td><td>${flightInfo.airlineName}</td></tr>
            <tr><td>Route:</td><td>${flightInfo.outbound.origin} ${flightInfo.isRoundTrip ? '↔' : '→'} ${flightInfo.outbound.destination}</td></tr>
            <tr><td>Outbound:</td><td>${flightInfo.outbound.date} • ${flightInfo.outbound.departure} - ${flightInfo.outbound.arrival}</td></tr>
            ${flightInfo.return ? `<tr><td>Return:</td><td>${flightInfo.return.date} • ${flightInfo.return.departure} - ${flightInfo.return.arrival}</td></tr>` : ''}
            <tr><td>Passengers:</td><td>${passengers}</td></tr>
            <tr class="cost-row"><td>Cost:</td><td>$${flightTotal.toLocaleString()}</td></tr>
          </table>
        </div>
        ` : ''}

        ${hotelList.map((hotel, idx) => `
        <div class="section">
          <div class="section-title">🏨 ${hotelList.length > 1 ? `Hotel ${idx + 1}` : 'Accommodation'}</div>
          <table>
            <tr><td>Hotel:</td><td>${hotel.name}</td></tr>
            <tr><td>Location:</td><td>${hotel.locationText}</td></tr>
            <tr><td>Check-in:</td><td>${hotel.checkIn}</td></tr>
            <tr><td>Check-out:</td><td>${hotel.checkOut}</td></tr>
            <tr><td>Duration:</td><td>${hotel.nights} night${hotel.nights > 1 ? 's' : ''}</td></tr>
            <tr><td>Rating:</td><td>${'⭐'.repeat(hotel.stars)} ${hotel.rating ? hotel.rating.toFixed(1) : ''}</td></tr>
            <tr class="cost-row"><td>Cost:</td><td>$${hotel.totalPrice.toLocaleString()}</td></tr>
          </table>
        </div>
        `).join('')}

        <div class="total-section">
          <table>
            ${flightInfo ? `<tr><td>Flights (${passengers} pax)</td><td style="text-align:right;">$${flightTotal.toLocaleString()}</td></tr>` : ''}
            ${hotelList.map(h => `<tr><td>Hotel (${h.nights} nights)</td><td style="text-align:right;">$${h.totalPrice.toLocaleString()}</td></tr>`).join('')}
            <tr class="grand-total">
              <td>TOTAL</td>
              <td style="text-align:right;">$${grandTotal.toLocaleString()}</td>
            </tr>
          </table>
        </div>

        <div class="footer">
          <p>Generated by Airgati • airgati.com</p>
          <p>Document created: ${new Date().toLocaleDateString()}</p>
        </div>
      </body>
      </html>
    `;
  };

  // ═══════════════════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════════════════
  const destination = flightInfo?.outbound?.destination || 'Trip';

  return (
    <div className="bcm-overlay" onClick={onClose}>
      <div className="bcm-modal" onClick={(e) => e.stopPropagation()}>
        
        {/* Header */}
        <div className="bcm-header">
          <h2>📦 Your Trip Summary</h2>
          <button className="bcm-close" onClick={onClose}>×</button>
        </div>

        <div className="bcm-body">
          
          {/* Trip Title */}
          <div className="bcm-trip-title">
            <div className="bcm-destination">🗽 {destination} Trip</div>
            <div className="bcm-trip-meta">
              {searchParams?.departureDate && (
                <span>📅 {searchParams.departureDate}{searchParams?.returnDate ? ` - ${searchParams.returnDate}` : ''}</span>
              )}
              <span>👥 {passengers} traveler{passengers > 1 ? 's' : ''}</span>
            </div>
          </div>

          {/* Flight Card */}
          {flightInfo && (
            <div className="bcm-item">
              <div className="bcm-item-header">
                <div className="bcm-item-type">
                  <div className="bcm-item-icon flight">✈️</div>
                  <div>
                    <div className="bcm-item-label">{flightInfo.isRoundTrip ? 'Round-Trip' : 'One-Way'} Flight</div>
                    <div className="bcm-item-name">{flightInfo.airlineName}</div>
                  </div>
                </div>
                <div className="bcm-item-price">
                  <div className="bcm-price-amount">${flightTotal.toLocaleString()}</div>
                  <div className="bcm-price-label">for {passengers} pax</div>
                </div>
              </div>

              <div className="bcm-item-details">
                <div className="bcm-detail">
                  <span>🛫</span> {flightInfo.outbound.origin} → {flightInfo.outbound.destination}
                </div>
                <div className="bcm-detail">
                  <span>📅</span> {flightInfo.outbound.date}
                </div>
                <div className="bcm-detail">
                  <span>⏱️</span> {flightInfo.outbound.duration || 'Direct'}
                </div>
                {flightInfo.outbound.stops === 0 && (
                  <div className="bcm-detail nonstop">
                    <span>✓</span> Nonstop
                  </div>
                )}
              </div>

              {/* Booking Sources */}
              <div className="bcm-book-sources">
                <span className="bcm-book-label">Book on:</span>
                <div className="bcm-source-buttons">
                  {flightInfo.bookingLinks?.length > 0 ? (
                    flightInfo.bookingLinks.slice(0, 3).map((link, idx) => (
                      <button 
                        key={idx}
                        className="bcm-source-btn"
                        onClick={() => handleBookFlight(link)}
                      >
                        {link.name || 'Book'} →
                      </button>
                    ))
                  ) : flightInfo.deepLink ? (
                    <button 
                      className="bcm-source-btn primary"
                      onClick={() => handleBookFlight({ url: flightInfo.deepLink })}
                    >
                      Google Flights →
                    </button>
                  ) : (
                    <button 
                      className="bcm-source-btn primary"
                      onClick={() => window.open('https://www.google.com/flights', '_blank')}
                    >
                      Search Flights →
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Hotel Cards */}
          {hotelList.map((hotel, idx) => (
            <div className="bcm-item" key={idx}>
              <div className="bcm-item-header">
                <div className="bcm-item-type">
                  <div className="bcm-item-icon hotel">🏨</div>
                  <div>
                    <div className="bcm-item-label">Hotel • {hotel.nights} night{hotel.nights > 1 ? 's' : ''}</div>
                    <div className="bcm-item-name">{hotel.name}</div>
                  </div>
                </div>
                <div className="bcm-item-price">
                  <div className="bcm-price-amount">${hotel.totalPrice.toLocaleString()}</div>
                  <div className="bcm-price-label">${hotel.pricePerNight}/night</div>
                </div>
              </div>

              <div className="bcm-item-details">
                <div className="bcm-detail">
                  <span>📍</span> {hotel.locationText}
                </div>
                <div className="bcm-detail">
                  <span>📅</span> {hotel.checkIn} - {hotel.checkOut}
                </div>
                <div className="bcm-detail">
                  <span>⭐</span> {'⭐'.repeat(hotel.stars)} {hotel.rating ? `(${hotel.rating.toFixed(1)})` : ''}
                </div>
              </div>

              {/* Booking Sources */}
              <div className="bcm-book-sources">
                <span className="bcm-book-label">Book on:</span>
                <div className="bcm-source-buttons">
                  {hotel.bookingLinks?.length > 0 ? (
                    hotel.bookingLinks.slice(0, 3).map((link, idx) => (
                      <button 
                        key={idx}
                        className="bcm-source-btn"
                        onClick={() => handleBookHotel(hotel, link)}
                      >
                        {link.name || 'Book'} →
                      </button>
                    ))
                  ) : (
                    <>
                      <button 
                        className="bcm-source-btn"
                        onClick={() => window.open(`https://www.booking.com/searchresults.html?ss=${encodeURIComponent(hotel.city)}`, '_blank')}
                      >
                        Booking.com →
                      </button>
                      <button 
                        className="bcm-source-btn"
                        onClick={() => window.open(`https://www.expedia.com/Hotel-Search?destination=${encodeURIComponent(hotel.city)}`, '_blank')}
                      >
                        Expedia →
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}

          {/* Total Section */}
          <div className="bcm-total-section">
            <div className="bcm-total-row">
              <div className="bcm-total-label">Package Total</div>
              <div className="bcm-total-amount">${grandTotal.toLocaleString()}</div>
            </div>
            <div className="bcm-total-breakdown">
              {flightInfo && <span>Flights: ${flightTotal.toLocaleString()}</span>}
              {hotelList.length > 0 && <span>Hotels: ${hotelTotal.toLocaleString()}</span>}
            </div>
          </div>

          {/* Tip */}
          <div className="bcm-tip">
            <span className="bcm-tip-icon">💡</span>
            <span>
              <strong>Tip:</strong> Prices may vary on partner sites. Book soon to lock in these rates!
            </span>
          </div>

          {/* Divider */}
          <div className="bcm-divider">
            <span>Keep Your Itinerary</span>
          </div>

          {/* Share Section */}
          <div className="bcm-share-section">
            
            {/* Email */}
            <div className="bcm-email-group">
              <input
                type="email"
                className="bcm-email-input"
                placeholder="Enter your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <button 
                className={`bcm-email-btn ${emailSent ? 'sent' : ''}`}
                onClick={handleSendEmail}
                disabled={sendingEmail}
              >
                {emailSent ? '✓ Sent!' : sendingEmail ? '...' : '📧 Send'}
              </button>
            </div>

            {/* Action Buttons */}
            <div className="bcm-action-buttons">
              <button className="bcm-action-btn pdf" onClick={handleDownloadPDF}>
                <span>📄</span> Download PDF
              </button>
              <button 
                className={`bcm-action-btn share ${linkCopied ? 'copied' : ''}`} 
                onClick={handleCopyLink}
              >
                <span>{linkCopied ? '✓' : '🔗'}</span> {linkCopied ? 'Copied!' : 'Copy Link'}
              </button>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default BookingConfirmationModal;
